import {
  Grid, Spacing, Message, ICON_FILE_DOCUMENT_MULTIPLE_OUTLINE, CardSkeleton, Card, ICON_ALERT_CIRCLE_OUTLINE, Text, FontWeight, DetailItemList,
} from '@doveit/bricks';
import React from 'react';
import {
  IntentStatus, OfferStatus, PropertyDocumentType,
  ReferenceType,
} from '../../../domain/types';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { Document } from '../../../providers/api/dtos';
import { usePropertyDocuments } from '../../hooks/use-property-documents/usePropertyDocuments';
import {
  MANDATORY_OFFER_DOCUMENT_TYPES, OPTIONAL_PROPERTY_DOCUMENT_TYPES, propertyDocumentMetadata,
} from '../../../document/constants';
import ManageDocumentAction from '../../../document/containers/manage-document-action/ManageDocumentAction';
import { IntentsProviderFilters } from '../../../providers/api/intent/intentProvider';
import useIntents from '../../../intent/hooks/use-intents/useIntents';
import useOffersByIntentId from '../../../offer/hooks/use-offers-by-intent-id/useOffersByIntentId';
import { useOfferDocuments } from '../../../offer/hooks/use-offer-documents/useOfferDocuments';
import { sortDocumentsByLabel } from '../../../utils';
import { useAgentByPropertyId } from '../../../hooks/use-agent/useAgent';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import { getMandatoryPropertyDocumentTypes } from '../../../document/utils/documentUtils';

const sortPropertyDocumentsByOrder = (a: PropertyDocumentType, b: PropertyDocumentType) => (propertyDocumentMetadata[a]?.order ?? 0) - (propertyDocumentMetadata[b]?.order ?? 0);

export interface PropertyDocumentsWidgetProps {
  propertyId: number,
  onDocumentUpserted?: (document: Document) => void,
  skipAml: boolean,
}

export const PROPERTY_DOCUMENTS_LOAD_ERROR_MESSAGE = "Non è stato possibile caricare i documenti dell'immobile.";
export const PROPERTY_DOCUMENT_ADD_ERROR_MESSAGE = 'Non è possibile aggiungere il documento.';

const PropertyDocumentsWidget: React.FC<PropertyDocumentsWidgetProps> = ({
  propertyId,
  onDocumentUpserted,
  skipAml,
}) => {
  const { userIsContentEditor, userIsAdmin } = useRBAC();
  const { data: propertyAgent } = useAgentByPropertyId(propertyId);
  const isPropertyAgentOrManager = useCurrentAgentIsSameAgentOrManager(propertyAgent?.id);

  const canShow = React.useMemo(() => userIsAdmin || userIsContentEditor || isPropertyAgentOrManager, [isPropertyAgentOrManager, userIsAdmin, userIsContentEditor]);

  const {
    data: propertyDocuments = [],
    isLoading: arePropertyDocumentsLoading,
    error: propertyDocumentsError,
    mutate: mutatePropertyDocuments,
  } = usePropertyDocuments(propertyId);

  const intentFilters: IntentsProviderFilters = React.useMemo(() => ({
    propertyId,
    status: IntentStatus.BOUGHT,
  }), [propertyId]);

  const { data: paginatedBoughtIntents } = useIntents(intentFilters);
  const { data: confirmedOffers = [] } = useOffersByIntentId(paginatedBoughtIntents?.content?.[0]?.id, OfferStatus.CONFIRMED);

  const hasAConfirmedOffer = React.useMemo(() => confirmedOffers.length > 0, [confirmedOffers.length]);

  const { data: offerDocuments = [], mutate: mutateOfferDocuments } = useOfferDocuments(hasAConfirmedOffer ? confirmedOffers[0].id : undefined);

  const onUpsertPropertyDocument = React.useCallback(async (document: Document) => {
    await mutatePropertyDocuments();

    onDocumentUpserted?.(document);
  }, [mutatePropertyDocuments, onDocumentUpserted]);

  const onUpsertOfferDocument = React.useCallback(async (_: Document) => {
    await mutateOfferDocuments();
  }, [mutateOfferDocuments]);

  if (!canShow) {
    return null;
  }

  if (propertyDocumentsError) {
    return (
      <Card aria-label="Errore nel caricamento dei documenti dell'immobile">
        <Card.Header
          title="Documenti"
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          color="critical"
        />
        <Card.Content>
          <Message
            message={PROPERTY_DOCUMENTS_LOAD_ERROR_MESSAGE}
          />
        </Card.Content>
      </Card>
    );
  }

  if (arePropertyDocumentsLoading) {
    return <CardSkeleton aria-label="Documenti immobile in caricamento" />;
  }

  return (
    <Card aria-label="Sezione documenti dell'immobile">
      <Card.Header
        title="Documenti"
        icon={{ path: ICON_FILE_DOCUMENT_MULTIPLE_OUTLINE }}
      />
      <Card.Content>
        <Grid gutter={150}>
          <Grid.Unit>
            <Text.BodySmall fontWeight={FontWeight.MEDIUM} transform="uppercase">Obbligatori</Text.BodySmall>
            <Spacing margin={[100, 0, 0]}>
              <DetailItemList
                columns={hasAConfirmedOffer ? 2 : 1}
                withoutDividers
              >
                {getMandatoryPropertyDocumentTypes(skipAml)
                  .sort(sortDocumentsByLabel)
                  .map((type) => {
                    const existingDocument = propertyDocuments.find((document) => document.type === type);
                    return (
                      <DetailItemList.Item
                        key={type}
                        aria-label={`document-row-${type}`}
                      >
                        <ManageDocumentAction
                          referenceId={existingDocument?.reference.id ?? propertyId.toString()}
                          referenceType={existingDocument?.reference.type ?? ReferenceType.PROPERTY}
                          referenceContext={ReferenceType.PROPERTY}
                          documentType={type}
                          document={existingDocument}
                          onCreate={onUpsertPropertyDocument}
                          onUpdate={onUpsertPropertyDocument}
                        />
                      </DetailItemList.Item>
                    );
                  })}
                {hasAConfirmedOffer && MANDATORY_OFFER_DOCUMENT_TYPES
                  .sort(sortDocumentsByLabel)
                  .map((type) => (
                    <DetailItemList.Item
                      key={type}
                      aria-label={`document-row-${type}`}
                    >
                      <ManageDocumentAction
                        key={type}
                        referenceId={confirmedOffers[0].id!.toString()}
                        referenceType={ReferenceType.OFFER}
                        documentType={type}
                        document={offerDocuments.find((document) => document.type === type)}
                        onCreate={onUpsertOfferDocument}
                        onUpdate={onUpsertOfferDocument}
                      />
                    </DetailItemList.Item>
                  ))}
              </DetailItemList>
            </Spacing>
          </Grid.Unit>
          <Grid.Unit>
            <Text.BodySmall fontWeight={FontWeight.MEDIUM} transform="uppercase">Facoltativi</Text.BodySmall>
            <Spacing margin={[100, 0, 0]}>
              <DetailItemList
                columns={hasAConfirmedOffer ? 2 : 3}
                withoutDividers
              >
                {OPTIONAL_PROPERTY_DOCUMENT_TYPES
                  .sort(sortPropertyDocumentsByOrder)
                  .map((type) => {
                    const existingDocument = propertyDocuments.find((document) => document.type === type);

                    return (
                      <DetailItemList.Item
                        key={type}
                        aria-label={`document-row-${type}`}
                      >
                        <ManageDocumentAction
                          referenceId={existingDocument?.reference.id ?? propertyId.toString()}
                          referenceType={existingDocument?.reference.type ?? ReferenceType.PROPERTY}
                          referenceContext={ReferenceType.PROPERTY}
                          documentType={type}
                          document={existingDocument}
                          onCreate={onUpsertPropertyDocument}
                          onUpdate={onUpsertPropertyDocument}
                        />
                      </DetailItemList.Item>
                    );
                  })}
              </DetailItemList>
            </Spacing>
          </Grid.Unit>
        </Grid>
      </Card.Content>
    </Card>
  );
};

export default PropertyDocumentsWidget;
